/**
 * EleringChart — Elering estfeed "Minu tootmine" stiilis bar-graafik
 * GRID mõõtepunktide energia visualiseerimiseks.
 *
 * Layout (vastavalt elering-chart-spec.md):
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ ⚡ ELEKTER                                                   │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Mõõtepunktid [dropdown]  Päev|Nädal|Kuu|Aasta  ‹ 2026 ›    │
 *   │ Miinimum 5kWh  Keskmine 50kWh  Maksimum 90kWh  KOKKU 600kWh │
 *   │ ○Tulp ○Joon ○Tabel  ☐Börsihind  ☑Summeeri      [Laadi alla]│
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ [BAR CHART — kuude tulbad]                                   │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Värvi-palett: teal #003E51, light bg #F2F7F8, grid #8CB8C0
 *
 * Copyright (c) 2025 Rohepaneel
 */
import { useState, useEffect, useMemo } from 'react';
import { useParks } from '../contexts/ParksContext';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from 'recharts';

const COL = {
  primary: '#003E51',
  secondary: '#006272',
  text: '#2E3238',
  textMuted: '#505862',
  grid: '#8CB8C0',
  bgLight: '#F2F7F8',
  border: '#DEE2E6',
};

const PERIODS = [
  { id: 'day',   label: 'Päev'  },
  { id: 'week',  label: 'Nädal' },
  { id: 'month', label: 'Kuu'   },
  { id: 'year',  label: 'Aasta' },
];

const FIELDS = [
  { id: 'energy_import', label: 'Võrgust (import)', color: '#b91c1c' },
  { id: 'energy_export', label: 'Võrku (eksport)',  color: '#15803d' },
  { id: 'energy_total',  label: 'Kokku',            color: '#003E51' },
];

// Eristatavad värvid mõõtepunktide jaoks (Elering-ish + lisad)
const SLOT_COLORS = [
  '#003E51', '#16a34a', '#dc2626', '#d97706', '#7c3aed',
  '#0891b2', '#be185d', '#0d9488', '#a16207', '#1d4ed8',
  '#9333ea', '#0e7490', '#65a30d', '#c2410c', '#4338ca',
];
const colorForSlot = (slot, idx) => SLOT_COLORS[idx % SLOT_COLORS.length];

// Selektor — mõõtepunktide valik dropdown
function MultiSelect({ parks, selected, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '8px 14px', minWidth: 220,
          background: 'white',
          border: `1px solid ${COL.border}`, borderRadius: 4,
          color: COL.text, fontSize: 14,
          fontFamily: 'Fira Sans, sans-serif',
          cursor: 'pointer', textAlign: 'left',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <span>{selected.length} mõõtepunkti(i) valitud</span>
        <span style={{ marginLeft: 12, color: COL.textMuted }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4,
          background: 'white', border: `1px solid ${COL.border}`,
          borderRadius: 4, minWidth: 260, zIndex: 100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxHeight: 360, overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', gap: 4, padding: 8, borderBottom: `1px solid ${COL.border}` }}>
            <button onClick={() => onChange(parks.map(p => p.slot))}
              style={miniBtnStyle}>Kõik</button>
            <button onClick={() => onChange([])} style={miniBtnStyle}>Tühi</button>
          </div>
          {parks.map(p => {
            const isOn = selected.includes(p.slot);
            return (
              <label key={p.slot} style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                padding: '6px 12px', cursor: 'pointer',
                fontSize: 13, color: COL.text,
                fontFamily: 'Fira Sans, sans-serif',
              }}>
                <input type="checkbox" checked={isOn} style={{ marginTop: 3 }}
                  onChange={() => onChange(isOn ? selected.filter(s => s !== p.slot) : [...selected, p.slot].sort((a, b) => a - b))} />
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                  <span>#{p.slot} {p.name}</span>
                  {p.eic && (
                    <span style={{ fontSize: 10, color: COL.textMuted, fontFamily: 'JetBrains Mono, monospace', marginTop: 1 }}>
                      {p.eic}
                    </span>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const miniBtnStyle = {
  padding: '4px 10px', fontSize: 12,
  border: `1px solid ${COL.border}`, borderRadius: 3,
  background: 'white', cursor: 'pointer',
  fontFamily: 'Fira Sans, sans-serif',
  color: COL.textMuted, flex: 1,
};

function fmtNum(v, d = 0) {
  if (v == null) return '—';
  return v.toLocaleString('et-EE', { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtPeriodLabel(ts, granularity) {
  const d = new Date(ts);
  if (granularity === 'year') {
    return d.toLocaleDateString('et-EE', { month: 'short' }) + ' ' + d.getFullYear();
  }
  if (granularity === 'month' || granularity === 'week') {
    return d.toLocaleDateString('et-EE', { day: 'numeric', month: 'short' });
  }
  return d.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit' });
}

export function EleringChart() {
  const { data: parksData } = useParks();
  const parks = useMemo(() =>
    (parksData?.parks || []).filter(p => p.name).map(p => ({
      slot: p.slot, name: p.name, online: p.online,
      eic: p._meta?.eic_code || null,
    })),
    [parksData]
  );

  const [selectedSlots, setSelectedSlots] = useState([]);
  const [period, setPeriod] = useState('year');
  // viewDate = mis kuupäev/kuu/aasta on valitud (period määrab milline granulaarsus)
  const [viewDate, setViewDate] = useState(new Date());
  const [field, setField] = useState('energy_import');
  const [chartType, setChartType] = useState('bar');    // 'bar' | 'line' | 'table'
  const [summeeri, setSummeeri] = useState(false);      // Default: iga mõõtepunkt eraldi (kõrvuti)
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth() + 1;  // 1-12
  const day = viewDate.getDate();

  // Navigeerimine: prev/next vastavalt valitud period-ile
  const navPrev = () => {
    const d = new Date(viewDate);
    if (period === 'year')      d.setFullYear(d.getFullYear() - 1);
    else if (period === 'month') d.setMonth(d.getMonth() - 1);
    else if (period === 'week')  d.setDate(d.getDate() - 7);
    else /* day */               d.setDate(d.getDate() - 1);
    setViewDate(d);
  };
  const navNext = () => {
    const d = new Date(viewDate);
    if (period === 'year')      d.setFullYear(d.getFullYear() + 1);
    else if (period === 'month') d.setMonth(d.getMonth() + 1);
    else if (period === 'week')  d.setDate(d.getDate() + 7);
    else /* day */               d.setDate(d.getDate() + 1);
    setViewDate(d);
  };

  // Kuvasilt praegusele vaatele
  const viewLabel = useMemo(() => {
    if (period === 'year')   return String(year);
    if (period === 'month')  return viewDate.toLocaleDateString('et-EE', { month: 'long', year: 'numeric' });
    if (period === 'week') {
      // Leia nädala esmaspäev ja pühapäev
      const d = new Date(viewDate);
      const dow = (d.getDay() + 6) % 7; // 0 = esmaspäev
      const mon = new Date(d);  mon.setDate(d.getDate() - dow);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      const fmt = (x) => x.toLocaleDateString('et-EE', { day: 'numeric', month: 'short' });
      return `${fmt(mon)} – ${fmt(sun)} ${sun.getFullYear()}`;
    }
    return viewDate.toLocaleDateString('et-EE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
  }, [period, viewDate, year]);

  // Vaikimisi vali kõik pargid esmakordsel laadimisel
  useEffect(() => {
    if (selectedSlots.length === 0 && parks.length > 0) {
      setSelectedSlots(parks.map(p => p.slot));
    }
  }, [parks, selectedSlots]);

  useEffect(() => {
    if (selectedSlots.length === 0) { setPoints([]); return; }
    let cancel = false;
    setLoading(true);
    const params = new URLSearchParams({
      slots: selectedSlots.join(','),
      granularity: period,
      year, month, day,
      device: 'grid_meter',
      field,
    });
    fetch(`/api/parks/energy-summary?${params}`)
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(j => { if (!cancel) setPoints(j.points || []); })
      .catch(() => { if (!cancel) setPoints([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [selectedSlots, period, year, month, day, field]);

  // Andmete järeltöötlus: lisa kuvanime + summa kõikidest slotidest
  const chartData = useMemo(() => {
    return points.map(p => {
      const row = { ...p, time: fmtPeriodLabel(p.ts, period) };
      let summa = 0;
      for (const k of Object.keys(p)) {
        if (k.startsWith('slot_')) summa += p[k] || 0;
      }
      row._summa = summa;
      return row;
    });
  }, [points, period]);

  // Statistika
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    const sums = chartData.map(d => d._summa).filter(v => v > 0);
    if (sums.length === 0) return null;
    const min = Math.min(...sums);
    const max = Math.max(...sums);
    const total = sums.reduce((a, b) => a + b, 0);
    const avg = total / sums.length;
    const minIdx = chartData.findIndex(d => d._summa === min);
    const maxIdx = chartData.findIndex(d => d._summa === max);
    return {
      min, max, avg, total,
      minLabel: chartData[minIdx]?.time, maxLabel: chartData[maxIdx]?.time,
    };
  }, [chartData]);

  const seriesColor = FIELDS.find(f => f.id === field)?.color || COL.primary;

  return (
    <div style={{
      background: COL.bgLight,
      borderRadius: 4,
      padding: 24,
      marginBottom: 24,
      fontFamily: 'Fira Sans, sans-serif',
      color: COL.text,
    }}>
      {/* Päis */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: `2px solid ${COL.secondary}`,
        paddingBottom: 12, marginBottom: 16,
      }}>
        <span style={{ fontSize: 20, color: COL.secondary }}>⚡</span>
        <h2 style={{
          margin: 0, fontSize: 18, fontWeight: 600,
          color: COL.text, letterSpacing: '0.05em',
        }}>
          ELEKTER
        </h2>
      </div>

      {/* Filtrid */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 12, color: COL.textMuted, marginBottom: 4 }}>Mõõtepunktid</div>
          <MultiSelect parks={parks} selected={selectedSlots} onChange={setSelectedSlots} />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0 }}>
          {PERIODS.map(p => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              style={{
                padding: '10px 16px 9px',
                background: 'transparent',
                color: period === p.id ? COL.secondary : COL.textMuted,
                border: 'none',
                borderTop: `1px solid ${period === p.id ? COL.secondary : 'transparent'}`,
                borderBottom: `2px solid ${period === p.id ? COL.secondary : 'transparent'}`,
                fontFamily: 'Fira Sans, sans-serif', fontSize: 14,
                cursor: 'pointer',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 12, color: COL.textMuted, marginBottom: 4 }}>Periood</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={navPrev} style={{ ...miniBtnStyle, flex: 'unset', padding: '6px 10px', color: COL.secondary }}>‹</button>
            <span style={{ fontSize: 14, fontWeight: 600, color: COL.text, minWidth: 140, textAlign: 'center' }}>{viewLabel}</span>
            <button onClick={navNext} style={{ ...miniBtnStyle, flex: 'unset', padding: '6px 10px', color: COL.secondary }}>›</button>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: COL.textMuted, marginBottom: 4 }}>Suund</div>
          <select value={field} onChange={e => setField(e.target.value)}
            style={{
              padding: '8px 14px', minWidth: 180,
              background: 'white', border: `1px solid ${COL.border}`, borderRadius: 4,
              color: COL.text, fontSize: 14, fontFamily: 'Fira Sans, sans-serif',
            }}>
            {FIELDS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </div>
      </div>

      {/* Statistika */}
      {stats && (
        <div style={{
          display: 'flex', gap: 20, flexWrap: 'wrap',
          margin: '16px 0', alignItems: 'baseline',
        }}>
          <StatCell title={`Miinimum ${stats.minLabel || ''}`} value={`${fmtNum(stats.min)} kWh`} />
          <StatCell title="Keskmine" value={`${fmtNum(stats.avg)} kWh`} />
          <StatCell title={`Maksimum ${stats.maxLabel || ''}`} value={`${fmtNum(stats.max)} kWh`} />
          <StatCell title={`KOKKU ${viewLabel}`} value={`${fmtNum(stats.total)} kWh`} border />
        </div>
      )}

      {/* Chart type & options */}
      <div style={{
        display: 'flex', gap: 18, alignItems: 'center',
        padding: '12px 0', borderTop: `1px solid ${COL.border}`,
        borderBottom: `1px solid ${COL.border}`,
        marginBottom: 16,
      }}>
        <Radio name="chartType" value="bar"   current={chartType} onChange={setChartType} label="Tulpgraafik" />
        <Radio name="chartType" value="line"  current={chartType} onChange={setChartType} label="Joongraafik" />
        <Radio name="chartType" value="table" current={chartType} onChange={setChartType} label="Tabel" />
        <span style={{ flex: 1 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={summeeri} onChange={e => setSummeeri(e.target.checked)} />
          Summeeri
        </label>
      </div>

      {/* Perioodi navigeerimine — sama loogika kui top-baar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <button onClick={navPrev}
          style={{ background: 'transparent', border: 'none', color: COL.secondary, fontSize: 16, cursor: 'pointer' }}>
          ‹ Eelmine
        </button>
        <span style={{ fontSize: 16, fontWeight: 600, color: COL.text }}>{viewLabel}</span>
        <button onClick={navNext}
          style={{ background: 'transparent', border: 'none', color: COL.secondary, fontSize: 16, cursor: 'pointer' }}>
          Järgmine ›
        </button>
      </div>

      {/* Graafik */}
      <div style={{ height: 400, background: 'white', padding: '12px 0', borderRadius: 4 }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: COL.textMuted }}>
            Laadin...
          </div>
        ) : chartData.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: COL.textMuted }}>
            Andmed puuduvad valitud perioodi kohta
          </div>
        ) : chartType === 'table' ? (
          <TableView data={chartData} parks={parks} selectedSlots={selectedSlots} summeeri={summeeri} />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid stroke={COL.grid} strokeDasharray="0" vertical={false} />
                <XAxis dataKey="time" stroke={COL.grid} tick={{ fill: COL.text, fontSize: 13 }} axisLine={{ stroke: COL.grid }} />
                <YAxis stroke={COL.grid} tick={{ fill: COL.text, fontSize: 13 }} axisLine={{ stroke: COL.grid }}
                  label={{ value: 'kWh', position: 'insideLeft', angle: -90, fill: COL.text, fontSize: 13 }}
                  tickFormatter={(v) => v.toLocaleString('et-EE')} />
                <Tooltip
                  contentStyle={{ background: 'white', border: `1px solid ${COL.grid}`, fontFamily: 'Fira Sans, sans-serif' }}
                  itemStyle={{ padding: '2px 0' }}
                  formatter={(value, name) => [`${fmtNum(value, 1)} kWh`, name]}
                  labelFormatter={(label) => label}
                />
                <Legend wrapperStyle={{ fontSize: 13, color: COL.text }} />
                {summeeri ? (
                  <Bar dataKey="_summa" name="Summeeritud andmed" fill={seriesColor} />
                ) : (
                  // Mitu tulpa kõrvuti (mitte virnastatult) — iga mõõtepunkt oma värviga
                  selectedSlots.map((slot, idx) => (
                    <Bar key={slot} dataKey={`slot_${slot}`}
                      name={(() => {
                        const p = parks.find(p => p.slot === slot);
                        if (!p) return `Slot ${slot}`;
                        return p.eic ? `${p.name} · ${p.eic}` : p.name;
                      })()}
                      fill={colorForSlot(slot, idx)}
                    />
                  ))
                )}
              </BarChart>
            ) : (
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid stroke={COL.grid} strokeDasharray="3 3" />
                <XAxis dataKey="time" stroke={COL.grid} tick={{ fill: COL.text, fontSize: 13 }} />
                <YAxis stroke={COL.grid} tick={{ fill: COL.text, fontSize: 13 }} tickFormatter={(v) => v.toLocaleString('et-EE')} />
                <Tooltip
                  contentStyle={{ background: 'white', border: `1px solid ${COL.grid}`, fontFamily: 'Fira Sans, sans-serif' }}
                  formatter={(v, name) => [`${fmtNum(v, 1)} kWh`, name]}
                />
                <Legend />
                {summeeri ? (
                  <Line dataKey="_summa" name="Summeeritud" stroke={seriesColor} strokeWidth={2} dot />
                ) : (
                  selectedSlots.map((slot, idx) => (
                    <Line key={slot} dataKey={`slot_${slot}`}
                      name={(() => {
                        const p = parks.find(p => p.slot === slot);
                        if (!p) return `Slot ${slot}`;
                        return p.eic ? `${p.name} · ${p.eic}` : p.name;
                      })()}
                      stroke={colorForSlot(slot, idx)}
                      strokeWidth={1.5} dot={false}
                    />
                  ))
                )}
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function StatCell({ title, value, border }) {
  return (
    <div style={{
      padding: '0 20px',
      borderLeft: border ? `1px solid ${COL.border}` : 'none',
      fontFamily: 'Fira Sans, sans-serif',
    }}>
      <span style={{ display: 'block', fontSize: 12, color: COL.textMuted, fontWeight: 400 }}>{title}</span>
      <div style={{ fontSize: 24, color: COL.textMuted, fontWeight: 400, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function Radio({ name, value, current, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: COL.text, cursor: 'pointer' }}>
      <input type="radio" name={name} checked={current === value} onChange={() => onChange(value)} />
      {label}
    </label>
  );
}

function TableView({ data, parks, selectedSlots, summeeri }) {
  return (
    <div style={{ overflow: 'auto', maxHeight: 400, padding: '0 8px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'Fira Sans, sans-serif' }}>
        <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
          <tr style={{ borderBottom: `2px solid ${COL.grid}` }}>
            <th style={{ padding: '8px 12px', textAlign: 'left', color: COL.text }}>Periood</th>
            {summeeri ? (
              <th style={{ padding: '8px 12px', textAlign: 'right', color: COL.text }}>Kokku (kWh)</th>
            ) : (
              selectedSlots.map(s => {
                const p = parks.find(p => p.slot === s);
                return (
                  <th key={s} style={{ padding: '8px 12px', textAlign: 'right', color: COL.text }}>
                    {p?.name || `Slot ${s}`}
                    {p?.eic && <div style={{ fontSize: 9, color: COL.textMuted, fontFamily: 'JetBrains Mono, monospace', fontWeight: 400 }}>{p.eic}</div>}
                  </th>
                );
              })
            )}
          </tr>
        </thead>
        <tbody>
          {data.map((r, idx) => (
            <tr key={r.ts} style={{ borderBottom: `1px solid ${COL.border}`, background: idx % 2 ? COL.bgLight : 'white' }}>
              <td style={{ padding: '6px 12px', color: COL.text }}>{r.time}</td>
              {summeeri ? (
                <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(r._summa, 1)}</td>
              ) : (
                selectedSlots.map(s => (
                  <td key={s} style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtNum(r[`slot_${s}`], 1)}
                  </td>
                ))
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default EleringChart;
